import { useEffect, useRef } from 'react';
import type { CanvasUiComponentMeta } from '../../api/modules/components';
import { fetchCanvasUiSource } from '../../api/modules/components';

/** 在图鉴中直接运行生成产物使用的 Canvas UI helper，确保预览与实际装配代码一致。 */
export function CanvasUiPreview({ component }: { component: CanvasUiComponentMeta }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasHeight = component.kind === 'intro' || component.kind === 'result' ? 640 : component.kind === 'toolbar' ? 190 : 250;
  useEffect(() => {
    let alive = true;
    let frame = 0;
    fetchCanvasUiSource().then((payload) => {
      if (!alive || !canvasRef.current) return;
      try {
        // helper 只写入 __PLAYGEN_CANVAS_UI，不依赖应用状态或 DOM。
        new Function('window', payload.source)(window);
        const api = (window as any).__PLAYGEN_CANVAS_UI;
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        if (!ctx || !api) return;
        const draw = () => {
          if (!alive) return;
          const W = canvas.width;
          const H = canvas.height;
          ctx.clearRect(0, 0, W, H);
          const bg = ctx.createLinearGradient(0, 0, 0, H);
          bg.addColorStop(0, component.kind === 'hud' ? '#8bd9f3' : '#12284c'); bg.addColorStop(1, component.kind === 'hud' ? '#d9f3fa' : '#071226');
          ctx.fillStyle = bg; ctx.fillRect(0, 0, W, H);
          if (component.kind === 'hud') {
            ctx.fillStyle = 'rgba(255,255,255,0.58)';
            [[42, 184, 24], [72, 184, 32], [305, 194, 28]].forEach(([cx, cy, r]) => { ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fill(); });
          }
          if (component.kind === 'button') {
            api.drawButton(ctx, { x: 55, y: H / 2 - 29, w: 250, h: 58, text: '开始游戏', icon: '▶', colors: ['#67d56f', '#299a4c'], pulse: 0.035, border: 'rgba(255,255,255,.9)' });
          } else if (component.kind === 'hud') {
            api.drawHud(ctx, { x: 8, y: 24, w: 344, h: 62, style: 'text', fontSize: 20, textColor: '#1d4268', outline: 'rgba(255,255,255,0.92)', outlineWidth: 3, items: [{ icon: '★', label: '得分', value: '120' }, { icon: '💧', label: '能量', value: '5/5' }, { icon: '●', label: '金币', value: '100' }] });
          } else if (component.kind === 'title') {
            api.drawTitle(ctx, { x: W / 2, y: H / 2, text: '水果消除', fontSize: 44, color: '#ffd15c' });
          } else if (component.kind === 'intro') {
            api.drawStartScreen(ctx, { width: W, height: H, title: '水果消除', subtitle: '交换相邻棋子，连消得分', buttonText: '开始游戏', heroText: '✦' });
          } else if (component.kind === 'result') {
            api.drawResultScreen(ctx, { width: W, height: H, title: '恭喜通关！', score: '得分 1280', ctaText: '立即下载' });
          } else if (component.kind === 'toolbar') {
            api.drawToolbar(ctx, { width: W, height: H, labels: ['音量', '重置', '开始游戏'] });
          } else {
            api.drawGuideHand(ctx, { image: null, x: W / 2, y: H / 2, size: 80 });
            api.drawTitle(ctx, { x: W / 2, y: H / 2, text: '需要 guide_hand 素材', fontSize: 23, color: '#d9eaff', outlineWidth: 2 });
          }
          frame = requestAnimationFrame(draw);
        };
        draw();
      } catch {
        // 图鉴是只读预览，运行时 helper 异常不应影响主页面。
      }
    });
    return () => { alive = false; if (frame) cancelAnimationFrame(frame); };
  }, [component]);

  return (
    <div className="flex flex-col items-center gap-3">
      <canvas ref={canvasRef} width={360} height={canvasHeight} className="h-auto max-h-[70vh] w-auto rounded-2xl border border-cyan-300/20 bg-slate-950 shadow-2xl" />
      <p className="max-w-sm text-center text-xs leading-5 text-slate-500">此处直接执行生成产物使用的 Canvas 组件；蓝图与模板通过同一 API 组装。</p>
    </div>
  );
}
